import type { SchemaValidationErrorItem } from '@takeshape/errors';
import {
  type LayerSchema,
  type ProjectSchemaJSON,
  type ServiceLayers,
  type UndefinedLayerStatus,
  UserSchema,
  validateSchema
} from '@takeshape/schema';
import { assert, type Entitlements } from '@takeshape/util';
import omit from 'lodash/omit';
import { create } from 'zustand';
import { memoizeByReference } from '../../common/utils/memoize-by-reference.js';
import type { ProjectSchemaCompat } from '../../compat/types.js';
import { compatSchemaToSchema } from '../../compat/utils.js';
import { getProjectEntitlements } from '../../project/selectors.js';
import { isEqualLooseUndefined } from '../../schema/util/is-equal-loose-undefined.js';
import { getReduxStore } from '../../store-reference.js';
import { postSchemaRoutine, setUseScratchForPreview } from '../actions.js';
import { SCRATCH_BRANCH } from '../constants.js';
import { getBranchByName, getCurrentBranch, getCurrentBranchName } from '../selectors.js';
import type { Layers } from '../types.js';

export type WorkingCopyStore = {
  workingCopyJSON: null | ProjectSchemaJSON;
  /**
   * @deprecated use workingCopy instead
   */
  workingCopyCompat: null | ProjectSchemaCompat;
  dataOrigin: null | string;
  userSchema: null | UserSchema;
  /**
   * The working copy with all layer queries/shapes/mutations from all services added on.
   * Unlike the runtime schema, this includes queries/shapes/mutations that are not used.
   * It is useful to look up queries/shapes/mutations that are not currently used but could be.
   */
  referenceSchema: null | ProjectSchemaJSON;
  serviceLayers: null | ServiceLayers;
  errors: SchemaValidationErrorItem[];
  hasPendingChanges: boolean;

  /**
   * Bypass schema update and set working copy directly.
   */
  setWorkingCopy: (data: ProjectSchemaCompat | null, dataOrigin: string | null) => void;
  setWorkingCopyHasPendingChanges: (hasPendingChanges: boolean) => void;
  setWorkingCopyErrors: (errors: SchemaValidationErrorItem[]) => void;
  /**
   * Clear the working copy state
   */
  reset: () => void;
  setServiceLayers: (serviceLayers: ServiceLayers | null) => void;
  updateSchemaModels: () => void;
  updateAfterDeploy: () => void;
  update: (newSchema: ProjectSchemaCompat, dataOrigin: string, andDeploy?: boolean) => Promise<void>;
  getReferenceSchema: () => ProjectSchemaJSON | null;
};

const ignoreInDirtyCheck = ['created', 'updated', 'version'];

export const selectIsDirty = memoizeByReference(
  (newSchema?: ProjectSchemaJSON, baseSchema?: ProjectSchemaJSON): boolean => {
    return Boolean(
      newSchema &&
        baseSchema &&
        !isEqualLooseUndefined(omit(newSchema, ignoreInDirtyCheck), omit(baseSchema, ignoreInDirtyCheck))
    );
  }
);

const collectLayerValues = <K extends 'queries' | 'mutations' | 'shapes'>(
  serviceLayers: ServiceLayers,
  key: K
): LayerSchema[K] => {
  return {
    ...Object.values(serviceLayers)
      .map((layer) => (layer.schema ? layer.schema[key] : {}))
      .reduce(
        (acc, val) => ({
          ...acc,
          ...val
        }),
        {}
      )
  };
};

const validate = async (
  schema: ProjectSchemaCompat | ProjectSchemaJSON,
  entitlements: Entitlements | null,
  layers: Layers
) => {
  assert(entitlements, 'no entitlements');
  return validateSchema(
    {
      async resolveLayer(id) {
        const layer = layers[id];
        if (!layer) {
          return { id, layer: undefined, status: 'disconnected' };
        }

        const { data: layerSchema, status } = layer;
        return layerSchema
          ? { id, schema: layerSchema, status: 'ok' }
          : {
              id,
              schema: undefined,
              status: status as UndefinedLayerStatus
            };
      },
      entitlements
    },
    'version' in schema ? compatSchemaToSchema(schema) : schema
  );
};

const buildReferenceSchema = (workingCopyJSON: ProjectSchemaJSON, serviceLayers: ServiceLayers) => {
  return {
    ...workingCopyJSON,
    shapes: {
      ...collectLayerValues(serviceLayers, 'shapes'),
      ...workingCopyJSON.shapes
    },
    queries: {
      ...collectLayerValues(serviceLayers, 'queries'),
      ...workingCopyJSON.queries
    },
    mutations: {
      ...collectLayerValues(serviceLayers, 'mutations'),
      ...workingCopyJSON.mutations
    }
  };
};

export const useWorkingCopyStore = create<WorkingCopyStore>((set, get) => ({
  workingCopyJSON: null,
  workingCopyCompat: null,
  version: null,
  dataOrigin: null,
  userSchema: null,
  referenceSchema: null,
  serviceLayers: null,
  errors: [],
  hasPendingChanges: false,

  setWorkingCopy(data: ProjectSchemaCompat | null, dataOrigin: string | null) {
    const { serviceLayers } = get();
    const workingCopyJSON = data && compatSchemaToSchema(data);

    set({
      workingCopyJSON,
      workingCopyCompat: data,
      userSchema: workingCopyJSON ? UserSchema.fromJson(workingCopyJSON, serviceLayers ?? {}) : null,
      dataOrigin,
      referenceSchema: null,
      serviceLayers,
      errors: [],
      hasPendingChanges: false
    });
  },

  setWorkingCopyHasPendingChanges(hasPendingChanges: boolean) {
    set({ hasPendingChanges });
  },

  setWorkingCopyErrors(errors: SchemaValidationErrorItem[]) {
    set({ errors });
  },

  reset() {
    const { setWorkingCopy, setServiceLayers } = get();
    setWorkingCopy(null, null);
    setServiceLayers(null);
  },

  setServiceLayers(serviceLayers: ServiceLayers | null) {
    const { updateSchemaModels } = get();
    set({
      serviceLayers
    });
    updateSchemaModels();
  },

  updateSchemaModels() {
    const { workingCopyJSON, serviceLayers } = get();
    set({
      userSchema: workingCopyJSON ? UserSchema.fromJson(workingCopyJSON, serviceLayers ?? {}) : null
    });
  },

  updateAfterDeploy() {
    const { workingCopyCompat } = get();
    if (workingCopyCompat) {
      set({
        workingCopyCompat: {
          ...workingCopyCompat,
          version: workingCopyCompat.version + 1
        }
      });
    }
  },

  /**
   * Updates the working copy and previews the new schema in the scratch branch.
   * This does not update the service layers.
   */
  update: async (newSchema: ProjectSchemaCompat, dataOrigin: string, andDeploy?: boolean) => {
    const reduxStore = getReduxStore();
    const reduxState = reduxStore.getState();
    const entitlements = getProjectEntitlements(reduxState);
    const currentBranchName = getCurrentBranchName(reduxState);
    const scratchBranch = getBranchByName(reduxState, SCRATCH_BRANCH);
    const currentBranch = getCurrentBranch(reduxState);
    const layers = scratchBranch?.layers.user.data ? scratchBranch.layers : currentBranch?.layers;
    const { workingCopyCompat, setWorkingCopy } = get();
    // Don't do anything if there was no change
    if (isEqualLooseUndefined(workingCopyCompat, newSchema)) {
      set({ hasPendingChanges: false });
      return;
    }

    // Validate the new schema. If it's invalid, don't save the change.
    const { valid, errors } = await validate(newSchema, entitlements, layers);

    if (!valid) {
      console.error('The schema is invalid:\n\n', newSchema, errors);
      set({ errors: errors ?? [], hasPendingChanges: false });
      return;
    }

    setWorkingCopy(newSchema, dataOrigin);

    reduxStore.dispatch(
      postSchemaRoutine.requested(newSchema, {
        branch: SCRATCH_BRANCH,
        runtime: true
      })
    );

    if (andDeploy) {
      reduxStore.dispatch(
        postSchemaRoutine.requested(newSchema, {
          branch: currentBranchName,
          runtime: true
        })
      );
    }

    reduxStore.dispatch(setUseScratchForPreview(true));
  },

  getReferenceSchema: () => {
    const { referenceSchema, workingCopyJSON, serviceLayers } = get();
    if (referenceSchema) {
      return referenceSchema;
    }
    if (!workingCopyJSON || !serviceLayers) {
      return null;
    }
    const newReferenceSchema = buildReferenceSchema(workingCopyJSON, serviceLayers);
    set({ referenceSchema: newReferenceSchema });
    return newReferenceSchema;
  }
}));
